import { useEffect, useRef, useState } from 'react'

export type LoaderPhase = 'pending' | 'loader' | 'done'

interface Options {
  /** Don't show the loader unless the work is still going after this. */
  delayMs?: number
  /** Once the loader IS shown, keep it at least this long so it can't blink. */
  minVisibleMs?: number
}

/**
 * Anti-flicker loader gating, shared by every page/section that fetches before
 * showing content (idea detail, report, list grids, profile).
 *
 * Returns a phase the caller renders against:
 *  - `pending` — still loading but within the delay window → render nothing
 *    (a height-stable placeholder), so a quick load never flashes a loader.
 *  - `loader`  — loading has outlasted the delay (or is riding out its minimum
 *    visible span) → render the spinner/skeleton, ideally with `page-route-enter`
 *    so it crossfades in.
 *  - `done`    — content is ready → render it (also crossfaded for a smooth hand-off).
 *
 * The minimum-visible hold means a loader that does appear never blinks out a
 * frame later; the hand-off to content is always a deliberate crossfade.
 */
export function useLoaderPhase(
  loading: boolean,
  { delayMs = 220, minVisibleMs = 400 }: Options = {},
): LoaderPhase {
  const [phase, setPhase] = useState<LoaderPhase>(loading ? 'pending' : 'done')
  const shownAtRef = useRef<number | null>(null)

  // The synchronous phase resets below are intentional: they react to a change
  // in `loading` (effect dep), not to every render, and React bails when the
  // value is unchanged. This is the standard reset-on-input-change pattern.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (loading) {
      setPhase('pending')
      shownAtRef.current = null
      const t = setTimeout(() => {
        shownAtRef.current = Date.now()
        setPhase('loader')
      }, delayMs)
      return () => clearTimeout(t)
    }

    // Loading finished. If the loader never showed, go straight to content.
    if (shownAtRef.current == null) {
      setPhase('done')
      return
    }
    // Otherwise hold the loader out the rest of its minimum visible span.
    const remaining = minVisibleMs - (Date.now() - shownAtRef.current)
    if (remaining <= 0) {
      shownAtRef.current = null
      setPhase('done')
      return
    }
    setPhase('loader')
    const t = setTimeout(() => {
      shownAtRef.current = null
      setPhase('done')
    }, remaining)
    return () => clearTimeout(t)
  }, [loading, delayMs, minVisibleMs])
  /* eslint-enable react-hooks/set-state-in-effect */

  return phase
}
